import {useCallback, useEffect, useRef, useState} from 'react';
import {store, useAppDispatch, useAppSelector} from 'store/customer';
import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import {useDebouncedCallback} from 'use-debounce';
import {xml2json, json2xml} from 'xml-js';
import {
    dataSet,
    templateSet,
    selectData,
    selectTemplate,
    clearPreview,
    reset,
    getIsSavingProduct,
    getIsRightHangProduct,
    setHasPreview3DLoading,
    setImageDataUrl,
} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {shallowEqual} from 'react-redux';
import usePreview3DData from 'components/customer/Preview3D/usePreview3DData';
import usePreview3D from 'components/customer/Preview3D/usePreview3D';
import {
    PreviewPosition,
    Batch,
    PreviewLocation,
    isBaseCornerCabinet,
    isCornerPantryCabinet,
} from 'components/customer/Preview3D/types';
import useDoorAndDrawerAnimation from 'components/customer/Preview3D/animation/useDoorAndDrawerAnimation';
import useCameraSetup from 'components/customer/Preview3D/lib/usePreview3DCameraSetup';
import useLightingSetup from 'components/customer/Preview3D/lib/useLightingSetup';
import useRendererSetup from 'components/customer/Preview3DCommon/lib/useRendererSetup';
import useMaterialTexture from 'components/customer/Preview3D/lib/useMaterialTexture';
import useImageDataUrlTrigger from 'components/customer/Preview3D/lib/useImageDataUrlTrigger';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';
import {
    normalizeLShapeCabinet,
    correctBaseCornerDoorMargins,
} from 'components/customer/Preview3DCommon/values/door';
import {isMobile} from 'react-device-detect';
import {detectShakerDoor} from 'components/customer/Preview3DCommon/helpers/shakerDoorHelper';
import {waitForInitialDoorPoses} from 'components/customer/Preview3DCommon/3d-models/shaker';
import useShakerDoors from 'components/customer/Preview3DCommon/hardware/useShakerDoor';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {getDoor} from 'components/customer/Materials/store/selectors/doorSelector';
import {MaterialType} from 'components/customer/Materials/entity';
import {TextGeometry} from 'three/examples/jsm/geometries/TextGeometry';
import {PRODUCT_ID} from 'shared/constants/Products';
import {ProductApi} from 'components/customer/Product/store/productApi';

// Fallback wait for the camera fit tweens (500ms) before announcing the page as
// rendered, so a stalled render loop can never withhold the screenshot event.
const FIT_SETTLE_TIMEOUT_MS = 1500;
// Same guard for the advanced doors the fit waits on.
const ADVANCED_DOORS_TIMEOUT_MS = 3000;

// Faded panel outlines go opaque in wireframe through the same
// onToggleWireframe handler RoomPlanner triggers.
const toggleSegmentLineWireframe = (
    obj: THREE.Object3D,
    isWireframeMode: boolean
) => {
    if (!(obj instanceof THREE.LineSegments)) return;

    const onToggleWireframe = obj.userData?.onToggleWireframe as
        ((isWireframe: boolean) => void) | undefined;
    onToggleWireframe?.(isWireframeMode);
};

const useThree = (
    previewLocation: PreviewLocation,
    expanded?: boolean,
    hidden?: boolean,
    removeDebounce?: boolean,
    raw?: boolean
) => {
    const dispatch = useAppDispatch();
    const previewData = useAppSelector(selectData, shallowEqual);
    const template = useAppSelector(selectTemplate, shallowEqual);
    const isSavingProduct = useAppSelector(getIsSavingProduct, shallowEqual);
    const isRightHangProduct = useAppSelector(getIsRightHangProduct);
    const previewRef = useRef<HTMLDivElement>();
    const scene = useRef<THREE.Scene>();
    const cabinetGroupRef = useRef<THREE.Group | null>(null);
    const initialized = useRef(false);
    const loaded = useRef(false);
    const animationFrameId = useRef<number | null>(null);

    const {values, productDataStore, adjustableLegsQuantity} = use3DContext();

    const [isLoading, setIsLoading] = useState(false);
    const isLoadingRef = useRef(false);
    const [templateFetched, setTemplateFetched] = useState(false);
    const [showTexture, setShowTexture] = useState(true);

    const {setupLighting} = useLightingSetup(scene);
    const {renderer, setupRenderer} = useRendererSetup({
        mountRef: previewRef,
        appendToMount: false,
        rendererParams: {
            preserveDrawingBuffer: true,
        },
        pixelRatio: window.devicePixelRatio,
        clearColor: '#fff',
        clearAlpha: 1,
        autoClear: false,
        domElementStyle: {
            borderRadius: '8px 0 0 8px',
        },
    });
    const {
        camera,
        setupCamera,
        setupCameraPosition,
        controls,
        setupOrbitControls,
        fitCameraToScene,
    } = useCameraSetup(previewRef, renderer, scene, expanded);

    const {
        buttonLabel,
        handleClick,
        animationHandler,
        clearHandlers,
        attachCornerDrawerAnimation,
        isDoorOpenRef,
        waitForSnapOpens,
    } = useDoorAndDrawerAnimation(
        ['DOORS', 'DRAWERS'].includes(previewLocation) ? false : true,
        hidden,
        raw,
        previewLocation
    );

    const {
        fieldDependencies,
        replaceDimensions,
        templateData,
        setupHardware,
        variables,
        hasError,
        errors,
        getShapedPanels,
        productType,
    } = usePreview3DData(scene, showTexture, previewLocation, cabinetGroupRef);
    const hasValidationErrors =
        errors && Boolean((errors as {fields: string[]}[])?.length);
    const showLeftIsoView = variables?.isLeftClip || variables?.isLeftRadius;
    const [previewPosition, setPreviewPosition] = useState<PreviewPosition>(
        previewLocation === 'DOORS'
            ? 'FRONT'
            : showLeftIsoView
              ? 'LEFT'
              : 'RIGHT'
    );

    const {loadDataXML, createSceneXML} = usePreview3D();
    const {
        exteriorTexture,
        exteriorEdgeTexture,
        carcaseTexture,
        carcaseEdgeTexture,
        exteriorThickness,
        carcaseThickness,
        carcaseSubstrateMaterial,
        exteriorSubstrateMaterial,
    } = useMaterialTexture();

    const {setImageDataUrlTrigger} = useImageDataUrlTrigger(renderer);
    const onLoadScene = useDebouncedCallback(() => {
        document.dispatchEvent(new Event('pageRendered'));
    }, 400);

    // Snap doors open, fit the camera, and only announce the page as rendered
    // once the fit tween has settled. Puppeteer screenshots on that event, so
    // firing it while the fit is still tweening captures a mid-zoom frame.
    // Advanced doors never reach waitForSnapOpens, so they are awaited here.
    const settleAndNotify = useCallback(
        async (advancedDoorsReady?: Promise<unknown> | null) => {
            if (advancedDoorsReady != null) {
                await Promise.race([
                    advancedDoorsReady,
                    new Promise((resolve) =>
                        setTimeout(resolve, ADVANCED_DOORS_TIMEOUT_MS)
                    ),
                ]);
            }

            await waitForSnapOpens();
            await Promise.race([
                fitCameraToScene(),
                new Promise((resolve) =>
                    setTimeout(resolve, FIT_SETTLE_TIMEOUT_MS)
                ),
            ]);
            onLoadScene();
        },
        []
    );

    const selectedExteriorMaterial = useAppSelector(
        (state) => getMaterial(state, MaterialType.EXTERIOR),
        shallowEqual
    );
    const selectedDoor = useAppSelector(
        (state) => getDoor(state, MaterialType.EXTERIOR, 0),
        shallowEqual
    );
    // Figure out which shaker door model to use
    const shakerDoorInfo = detectShakerDoor(
        selectedExteriorMaterial,
        selectedDoor
    );
    const {addShakerDoorWithAutoPositioning} = useShakerDoors(
        showTexture,
        previewLocation
    );

    // Track shaker door animation state separately
    const [shakerDoorsOpen, setShakerDoorsOpen] = useState(
        previewLocation === 'DOORS' || previewLocation === 'DRAWERS'
            ? false
            : true
    );
    const shakerDoorMeshes = useRef<THREE.Group[]>([]);

    // Animate shaker doors
    const animateShakerDoors = useCallback((open: boolean) => {
        shakerDoorMeshes.current.forEach((door) => {
            if (
                door.userData.toggleDoor &&
                typeof door.userData.toggleDoor === 'function'
            ) {
                (
                    door.userData.toggleDoor as (params: {
                        isDoorOrDrawerOpen: boolean;
                    }) => void
                )({isDoorOrDrawerOpen: open});
            }
        });
        setShakerDoorsOpen(open);
    }, []);

    // Handle click for both regular doors and shaker doors
    const handleClickWithShakerDoors = useCallback(() => {
        // Animate regular doors
        handleClick();

        // Animate shaker doors too
        const newState = !shakerDoorsOpen;
        animateShakerDoors(newState);
    }, [handleClick, shakerDoorsOpen, animateShakerDoors]);

    // effect to reset the default open state of doors when preview is rerendered
    useEffect(() => {
        if (isLoading) {
            const isDoorOpen =
                !['DOORS', 'DRAWERS'].includes(previewLocation) &&
                values?.cabinet_type !== PRODUCT_ID.DRAWER_COMPONENT;
            isDoorOpenRef.current = isDoorOpen;
            setShakerDoorsOpen(isDoorOpen);
        }
    }, [isLoading]);

    const updateMaterials = useCallback((textureValue: boolean) => {
        try {
            const root = scene.current;
            if (!root) return;
            root.traverse((obj) => {
                toggleSegmentLineWireframe(obj, !textureValue);

                if (obj instanceof THREE.Mesh) {
                    if (obj.name === 'Adjustable_leg') {
                        obj.visible = true;
                        return;
                    }

                    if (obj.geometry instanceof TextGeometry) {
                        obj.visible = true;
                        return;
                    }

                    const hasLineSegmentsChild = obj.children.some(
                        (child) => child instanceof THREE.LineSegments
                    );

                    if (!hasLineSegmentsChild) {
                        obj.visible = textureValue;
                    }
                }

                if (
                    obj instanceof THREE.LineSegments &&
                    obj.parent instanceof THREE.Mesh
                ) {
                    const parentMesh = obj.parent as THREE.Mesh;

                    if (!parentMesh.material) return;

                    if (Array.isArray(parentMesh.material)) {
                        parentMesh.material.forEach((mat) => {
                            mat.visible = textureValue;
                        });
                    } else {
                        parentMesh.material.visible = textureValue;
                    }
                }
            });
        } catch (error) {
            reloadScene();
        }
    }, []);

    const createScene = useCallback(
        (batch: Batch) => {
            scene.current = new THREE.Scene();
            const manager = new THREE.LoadingManager();
            const cabinetGroup = createSceneXML(
                batch,
                animationHandler,
                manager,
                showTexture,
                typeof values.include_drawer_faces !== 'undefined'
                    ? Boolean(values.include_drawer_faces)
                    : true,
                previewLocation !== 'DRAWERS',
                previewLocation === 'SHELVES',
                previewLocation,
                values
            );

            cabinetGroupRef.current = cabinetGroup;

            // Advanced doors are skipped by the XML pass and built here, so the
            // fit must wait for them or it frames a door-less, closed cabinet.
            let advancedDoorsAdded = false;
            const advancedDoorsReady = shakerDoorInfo.isShakerDoor
                ? addShakerDoorWithAutoPositioning(variables)
                      .then(async (shakerDoors) => {
                          if (!shakerDoors?.length) return;

                          // Store shaker doors for animation
                          shakerDoorMeshes.current = shakerDoors;
                          shakerDoors.forEach((door) => {
                              scene.current.add(door);
                          });
                          advancedDoorsAdded = true;

                          // Their open state only lands on the next TWEEN tick.
                          await waitForInitialDoorPoses(shakerDoors);
                      })
                      .catch(() => {
                          // Silently handle shaker door loading errors
                      })
                : null;

            // load the cabinet group once texture is ready
            let sceneAdded = false;
            const onLoadHandler = () => {
                if (sceneAdded) return;
                sceneAdded = true;

                const meshes = getShapedPanels() ?? [];
                meshes?.forEach((mesh) => {
                    cabinetGroup.add(mesh);
                    if (mesh.userData?.isCornerDrawerPart) {
                        attachCornerDrawerAnimation(
                            mesh,
                            mesh.position,
                            Number(mesh.userData?.index)
                        );
                    }
                });
                // Shaped panel outlines are built faded regardless of
                // showTexture, so make them opaque when built in wireframe.
                if (!showTexture) {
                    cabinetGroup.traverse((child) =>
                        toggleSegmentLineWireframe(child, true)
                    );
                }
                isLoadingRef.current = false;
                setIsLoading(false);
                if (hidden && !isSavingProduct) {
                    setImageDataUrlTrigger();
                }
                scene.current.add(cabinetGroup);
                // Wait for advanced doors and snap-opens so fit measures an
                // open-state bbox.
                void settleAndNotify(advancedDoorsReady);
            };

            manager.onLoad = onLoadHandler;

            // if wireframe view we need to trigger the onload manually
            // UShaped Panel don't render based on the xml so load it manually
            if (!showTexture || variables.isUShapedPanel) {
                onLoadHandler();
            } else {
                // Mount immediately with placeholder panel materials; textures
                // upgrade asynchronously on each mesh (avoid waiting for every
                // decode before the cabinet appears).
                queueMicrotask(() => onLoadHandler());
            }
            if (advancedDoorsReady != null) {
                void advancedDoorsReady.then(() => {
                    // drawer component will not trigger onLoadHandler if it is a shaker door so trigger required stuff
                    if (values?.cabinet_type === PRODUCT_ID.DRAWER_COMPONENT) {
                        isLoadingRef.current = false;
                        setIsLoading(false);
                        if (hidden && !isSavingProduct) {
                            setImageDataUrlTrigger();
                        }
                        void settleAndNotify();
                        return;
                    }

                    // Backstop for a fit that timed out waiting on the doors.
                    if (advancedDoorsAdded) void settleAndNotify();
                });
            }
        },
        [
            ...fieldDependencies,
            getShapedPanels,
            animationHandler,
            isSavingProduct,
            attachCornerDrawerAnimation,
            setImageDataUrlTrigger,
            variables,
            createSceneXML,
            showTexture,
            previewLocation,
            values,
        ]
    );

    const reloadScene = useDebouncedCallback(
        () => {
            if (template) {
                const parsedJson = replaceDimensions(template);
                const data = loadDataXML(
                    json2xml(parsedJson, {compact: false})
                ) as Batch;

                // Apply the same L-shape geometry corrections the RoomPlanner
                // uses so the standalone Preview3D matches the 3DRP room.
                const lShapeInputs = {
                    cabinetLeftDepth: Number(values.cabinet_left_depth),
                    cabinetRightDepth: Number(values.cabinet_right_depth),
                    cabinetLeftWidth: Number(values.cabinet_left_width),
                    cabinetRightWidth: Number(values.cabinet_right_width),
                    cabinetHeight: Number(values.cabinet_height),
                    doorGap: Number(values.cabinet_door_gap) || 0,
                    doorTop: Number(values.cabinet_door_top) || 0,
                    doorBottom: Number(values.cabinet_door_bottom) || 0,
                    doorLeft: Number(values.cabinet_door_left) || 0,
                    doorRight: Number(values.cabinet_door_right) || 0,
                    doorThickness: Number(exteriorThickness),
                    carc: Number(carcaseThickness),
                    isBaseCorner: isBaseCornerCabinet(variables, values),
                    isCornerPantry: isCornerPantryCabinet(variables, values),
                };

                normalizeLShapeCabinet(
                    data,
                    templateData,
                    variables,
                    lShapeInputs
                );
                correctBaseCornerDoorMargins(data, lShapeInputs);

                clearHandlers();

                dispatch(dataSet(data));
            }
        },
        removeDebounce ? 0 : 300
    );

    const loadXml = useDebouncedCallback(
        async () => {
            if (productType) {
                dispatch(clearPreview());

                const template3d = productDataStore.current?.template_3d;

                let xmlContent: string | undefined;
                if (Array.isArray(template3d) && template3d.length > 1) {
                    xmlContent = template3d[1] as string;
                } else {
                    const response = await store.dispatch(
                        ProductApi.endpoints.getPreviewTemplate.initiate(
                            {
                                productId: productType,
                            },
                            {
                                subscribe: false,
                            }
                        )
                    );
                    if (response.data) {
                        xmlContent = response.data[1];
                    }
                }

                if (xmlContent != undefined) {
                    const json = xml2json(xmlContent, {
                        compact: false,
                    });
                    dispatch(templateSet(json));
                }
                setTemplateFetched(true);
                reloadScene();
            }
        },
        removeDebounce ? 0 : 800
    );

    const loadPreview3D = useDebouncedCallback(
        useCallback(
            (data: Batch) => {
                if (previewRef.current) {
                    createScene(data);
                    setupLighting();
                    setupHardware();
                }
            },
            [
                ...fieldDependencies,
                adjustableLegsQuantity,
                showTexture,
                carcaseTexture,
                carcaseEdgeTexture,
                exteriorTexture,
                exteriorEdgeTexture,
                carcaseSubstrateMaterial,
                exteriorSubstrateMaterial,
                values.cabinet_include_hardware,
                createScene,
                isSavingProduct,
            ]
        ),
        removeDebounce ? 0 : 800
    );

    const initialize = useCallback(() => {
        if (previewRef.current) {
            setupCamera();
            setupCameraPosition(previewPosition);

            scene.current = new THREE.Scene();

            const rendererLocal = setupRenderer();
            if (!rendererLocal.current) return;

            previewRef.current.appendChild(rendererLocal.current.domElement);

            setupOrbitControls();
        }
    }, []);

    const animate = useCallback(() => {
        animationFrameId.current = requestAnimationFrame(animate);

        if (
            !renderer.current ||
            !scene.current ||
            !camera.current ||
            !controls.current
        )
            return;

        controls.current.update();
        renderer.current.clear();
        renderer.current.clearDepth();
        renderer.current.render(scene.current, camera.current);
        TWEEN.update();
    }, []);

    const handlePreview3DLoading = (previewData: Batch) => {
        dispatch(setHasPreview3DLoading(true));
        loadPreview3D(previewData);
    };

    useEffect(() => {
        return () => {
            initialized.current = false;
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
                animationFrameId.current = null;
            }
            controls.current?.dispose();
            if (previewRef.current && renderer.current?.domElement) {
                previewRef.current.removeChild(renderer.current.domElement);
            }
            renderer.current?.forceContextLoss();
            renderer.current?.dispose();
            dispatch(reset());
        };
    }, []);

    useEffect(() => {
        if (previewData != null && templateFetched && !hasError.current) {
            if (!isSavingProduct || hidden) {
                isLoadingRef.current = true;
                setIsLoading(true);
                loaded.current = true;
                handlePreview3DLoading(previewData as Batch);
            }
        }

        // Until this mount's template is fetched, previewData can still be the
        // previous product's (the store outlives client-side navigation).
        if (
            hasError.current &&
            !loaded.current &&
            previewData != null &&
            templateFetched
        ) {
            if (!isSavingProduct || hidden) {
                handlePreview3DLoading(previewData as Batch);
            }
        }
    }, [
        previewData,
        templateFetched,
        errors,
        hidden,
        isSavingProduct,
        loadPreview3D,
    ]);

    useEffect(() => {
        if (previewData != null && (!isSavingProduct || hidden)) {
            reloadScene();
        }
    }, [...fieldDependencies, hidden, isSavingProduct]);

    useEffect(() => {
        if (
            hidden &&
            isSavingProduct &&
            previewData != null &&
            templateFetched &&
            !isLoading &&
            !isLoadingRef.current
        ) {
            dispatch(setHasPreview3DLoading(true));
            setImageDataUrlTrigger();
        }
    }, [
        hidden,
        isSavingProduct,
        previewData,
        templateFetched,
        isLoading,
        setImageDataUrlTrigger,
        dispatch,
    ]);

    // A right-hung door swings toward +x, where the default 'RIGHT' iso camera
    // sits, so the fit measures it between the viewer and the carcase. The LEFT
    // iso mirrors the left-hang case and frames both variants identically.
    // Latched per mount so the user can still switch views afterwards.
    const rightHangViewApplied = useRef(false);
    useEffect(() => {
        // The DOORS tab is deliberately face-on.
        if (previewLocation === 'DOORS') return;
        if (!isRightHangProduct || !camera.current) return;
        if (rightHangViewApplied.current) return;

        rightHangViewApplied.current = true;
        setPreviewPosition('LEFT');
        setupCameraPosition('LEFT');
        // Resets zoom/target to the view baseline, so refit in case the scene
        // was already loaded and fitted.
        void fitCameraToScene();
    }, [isRightHangProduct]);

    const handlePreviewRef = useCallback((ref: HTMLDivElement) => {
        previewRef.current = ref;

        if (
            previewRef?.current &&
            previewRef.current.clientHeight !== 0 &&
            !initialized.current
        ) {
            isLoadingRef.current = true;
            setIsLoading(true);

            // Drop the previous product's model now rather than after
            // loadXml's debounce, so it can't be drawn in the meantime.
            dispatch(clearPreview());
            void loadXml();
            void initialize();
            void animate();

            initialized.current = true;
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (
                !camera.current ||
                !renderer.current ||
                !previewRef.current ||
                !isMobile
            )
                return;

            const width = previewRef.current.clientWidth;
            const height = previewRef.current.clientHeight;
            const aspect = width / height;

            if (camera.current instanceof THREE.OrthographicCamera) {
                const viewSize = 800;
                camera.current.left = -viewSize * aspect;
                camera.current.right = viewSize * aspect;
            }
            camera.current.updateProjectionMatrix();
            // Resize renderer
            renderer.current.setSize(width, height);
            renderer.current.setPixelRatio(window.devicePixelRatio);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', () =>
            setTimeout(handleResize, 100)
        );

        handleResize(); // Initial sizing

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    useEffect(() => {
        if (hidden) {
            dispatch(setImageDataUrl(''));
        }
    }, [hidden]);

    return {
        handleClick: handleClickWithShakerDoors,
        previewRef,
        setupCameraPosition,
        reloadScene,
        buttonLabel,
        previewPosition,
        setPreviewPosition,
        showTexture,
        isDoorOpenRef,
        variables,
        hasValidationErrors,
        isLoading,
        handlePreviewRef,
        updateMaterials,
        renderer,
        setShowTexture,
    };
};

export default useThree;
